/**
 * Gate for the firm console. Anonymous users get the auth modal and are
 * bounced to /. Signed-in users who aren't firm members are also bounced to
 * / (they belong in the standard app). Firm membership = a firm_members row,
 * resolved by useFirm.
 */

import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFirm } from '../hooks/useFirm';

export const RequireFirmRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, isAnonymous, openAuthModal } = useAuth();
  const { firm, loading: firmLoading, error: firmError } = useFirm();

  useEffect(() => {
    if (!loading && isAnonymous && !user) {
      openAuthModal({ headline: 'Sign in to open your firm console' });
    }
  }, [loading, isAnonymous, user, openAuthModal]);

  if (loading || firmLoading) return null;
  if (!user) return <Navigate to="/" replace />;
  // Only bounce when we're sure the user isn't a firm member. If the lookup
  // itself failed, show a retry instead of falsely sending a member home.
  if (!firm) {
    if (firmError) {
      return (
        <div className="mx-auto max-w-md py-32 text-center">
          <h1 className="mb-2 text-xl font-semibold text-gray-900">Couldn't load your firm console</h1>
          <p className="mb-6 text-sm text-gray-500">
            We hit a problem checking your firm access. This is usually temporary.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700"
          >
            Retry
          </button>
        </div>
      );
    }
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};
